import {
  type ComponentType,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from 'react';
import { type LucideIcon } from 'lucide-react';
import {
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenuCheckboxItem,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import { cn } from '@/lib/utils';

type MenuPrimitives = {
  Item: any;
  Separator: any;
  Sub: any;
  SubTrigger: any;
  SubContent: any;
  CheckboxItem: any;
  RadioGroup: any;
  RadioItem: any;
  MenuLabel: any;
};

export const dropdownPrimitives: MenuPrimitives = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
  Sub: DropdownMenuSub,
  SubTrigger: DropdownMenuSubTrigger,
  SubContent: DropdownMenuSubContent,
  CheckboxItem: DropdownMenuCheckboxItem,
  RadioGroup: DropdownMenuRadioGroup,
  RadioItem: DropdownMenuRadioItem,
  MenuLabel: DropdownMenuLabel,
};

export const contextPrimitives: MenuPrimitives = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubContent: ContextMenuSubContent,
  CheckboxItem: ContextMenuCheckboxItem,
  RadioGroup: ContextMenuRadioGroup,
  RadioItem: ContextMenuRadioItem,
  MenuLabel: ContextMenuLabel,
};

export enum ActionKind {
  Item = 'item',
  Submenu = 'submenu',
  Separator = 'separator',
  Custom = 'custom',
  Toggle = 'toggle',
  RadioGroup = 'radioGroup',
  MenuLabel = 'menuLabel',
}

// Accept either a component type (e.g. Lucide icon or your own)
// or a React Element (e.g. <SomeIcon />)
type IconComponent = ComponentType<{ className?: string }>;
type IconElement = ReactElement<{ className?: string }>;
export type IconProp = LucideIcon | IconComponent | IconElement;

const renderIcon = (icon: IconProp | undefined, className: string) => {
  if (!icon) {
    return null;
  }
  if (isValidElement(icon)) {
    return cloneElement(icon, { className: cn((icon.props as any)?.className, className) });
  }
  const Comp = icon as IconComponent;
  return <Comp className={className} />;
};

const MenuPrimitivesContext = createContext<MenuPrimitives | null>(null);

export const MenuProvider = ({ primitives, children }: PropsWithChildren<{ primitives: MenuPrimitives }>) => (
  <MenuPrimitivesContext.Provider value={primitives}>{children}</MenuPrimitivesContext.Provider>
);

export const useMenuPrimitives = () => {
  const primitives = useContext(MenuPrimitivesContext);
  if (!primitives) {
    throw new Error('Menu primitives missing. Wrap menu content with <MenuProvider>.');
  }
  return primitives;
};

const getDangerClasses = (primitives: MenuPrimitives) =>
  cn(
    'text-red-600! dark:text-red-500!',
    primitives === dropdownPrimitives
      ? 'hover:bg-red-100! hover:text-red-600! dark:hover:bg-red-950! dark:hover:text-red-600'
      : 'focus:bg-red-100/50! dark:focus:bg-red-950/50!',
  );

type SharedMenuItemProps = {
  icon?: IconProp;
  iconClassName?: string;
  appendIcon?: IconProp;
  appendIconClassName?: string;
  labelClassName?: string;
  className?: string;
  dataCy?: string;
  hidden?: boolean;
  disabled?: boolean;
  danger?: boolean;
};

type BaseMenuItemProps = SharedMenuItemProps & {
  stayOpen?: boolean;
  shortcut?: string;
  onSelect?: () => void;
  onClick?: () => void;
  children: ReactNode;
};

type InternalMenuItemProps = BaseMenuItemProps & { primitives: MenuPrimitives };

const MenuItemBase = ({
  primitives,
  icon,
  appendIcon,
  iconClassName,
  appendIconClassName,
  labelClassName,
  className,
  dataCy,
  disabled,
  danger,
  stayOpen,
  shortcut,
  onSelect,
  onClick,
  children,
}: InternalMenuItemProps) => {
  const dangerClasses = getDangerClasses(primitives);
  const ItemPrimitive = primitives.Item;

  return (
    <ItemPrimitive
      disabled={disabled}
      data-cy={dataCy}
      onSelect={(event: Event) => {
        if (stayOpen) {
          event.preventDefault();
        }

        if (!disabled) {
          (onSelect ?? onClick)?.();
        }
      }}
      className={cn('flex gap-x-2', danger && dangerClasses, className)}
    >
      {renderIcon(icon, cn('icon-6', danger ? 'text-red-600 dark:text-red-500' : 'text-icon-base', iconClassName))}
      <span className={cn('line-clamp-1 grow break-all', labelClassName)}>{children}</span>
      {renderIcon(
        appendIcon,
        cn('icon-4', danger ? 'text-red-600 dark:text-red-500' : 'text-icon-base', appendIconClassName),
      )}
      {shortcut && <kbd className="text-xs">{shortcut}</kbd>}
    </ItemPrimitive>
  );
};

export type MenuItemProps = BaseMenuItemProps;

export const MenuItem = (props: MenuItemProps) => {
  const primitives = useMenuPrimitives();

  return <MenuItemBase {...props} primitives={primitives} />;
};

type MenuSeparatorProps = { hidden?: boolean };

export const MenuSeparator = ({ hidden }: MenuSeparatorProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const SeparatorPrimitive = primitives.Separator;

  return <SeparatorPrimitive />;
};

type MenuLabelProps = {
  hidden?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
};

export const MenuLabel = ({ hidden, className, title, children }: MenuLabelProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const LabelPrimitive = primitives.MenuLabel;
  const computedTitle = title ?? (typeof children === 'string' ? children : undefined);

  return (
    <LabelPrimitive className={cn('line-clamp-1 wrap-anywhere', className)} title={computedTitle}>
      {children}
    </LabelPrimitive>
  );
};

type MenuCheckboxItemProps = SharedMenuItemProps & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
};

export const MenuCheckboxItem = ({
  icon,
  iconClassName,
  labelClassName,
  className,
  dataCy,
  hidden,
  disabled,
  danger,
  checked,
  onCheckedChange,
  children,
}: MenuCheckboxItemProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const CheckboxPrimitive = primitives.CheckboxItem;
  const dangerClasses = getDangerClasses(primitives);

  return (
    <CheckboxPrimitive
      data-cy={dataCy}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(value: boolean | 'indeterminate') => {
        if (!disabled) {
          onCheckedChange(Boolean(value));
        }
      }}
      className={cn(danger && dangerClasses, className)}
    >
      {renderIcon(icon, cn('mr-2 icon-6', danger ? 'text-red-600 dark:text-red-500' : 'text-icon-base', iconClassName))}
      <span className={cn('whitespace-nowrap', labelClassName)}>{children}</span>
    </CheckboxPrimitive>
  );
};

type MenuRadioGroupProps = {
  value: string;
  onValueChange: (value: string) => void;
  hidden?: boolean;
  className?: string;
  children: ReactNode;
};

export const MenuRadioGroup = ({ value, onValueChange, hidden, className, children }: MenuRadioGroupProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const RadioGroupPrimitive = primitives.RadioGroup;

  return (
    <RadioGroupPrimitive value={value} onValueChange={onValueChange} className={className}>
      {children}
    </RadioGroupPrimitive>
  );
};

type MenuRadioItemProps = {
  value: string;
  hidden?: boolean;
  className?: string;
  children: ReactNode;
  disabled?: boolean;
};

export const MenuRadioItem = ({ value, hidden, className, children, disabled }: MenuRadioItemProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const RadioItemPrimitive = primitives.RadioItem;

  return (
    <RadioItemPrimitive value={value} className={className} disabled={disabled}>
      {children}
    </RadioItemPrimitive>
  );
};

type MenuSubProps = SharedMenuItemProps & {
  triggerClassName?: string;
  contentClassName?: string;
  label: ReactNode;
  children: ReactNode;
};

export const MenuSub = ({
  icon,
  iconClassName,
  labelClassName,
  className,
  triggerClassName,
  contentClassName,
  hidden,
  disabled,
  danger,
  label,
  children,
}: MenuSubProps) => {
  const primitives = useMenuPrimitives();

  if (hidden) {
    return null;
  }

  const SubPrimitive = primitives.Sub;
  const SubTriggerPrimitive = primitives.SubTrigger;
  const SubContentPrimitive = primitives.SubContent;
  const dangerClasses = getDangerClasses(primitives);

  return (
    <SubPrimitive>
      <SubTriggerPrimitive disabled={disabled} className={cn(danger && dangerClasses, className, triggerClassName)}>
        {renderIcon(
          icon,
          cn('mr-2 text-icon-base icon-6', danger ? 'text-red-600 dark:text-red-500' : undefined, iconClassName),
        )}
        <span className={cn('whitespace-nowrap', labelClassName)}>{label}</span>
      </SubTriggerPrimitive>
      <SubContentPrimitive className={contentClassName}>
        <MenuProvider primitives={primitives}>{children}</MenuProvider>
      </SubContentPrimitive>
    </SubPrimitive>
  );
};
